import type { DeliveryAddress } from '@/api/types';

/**
 * Handing the destination to whatever the rider already uses.
 *
 * There is no map in this app and there should not be one: a tile provider
 * wants an API key and a budget, and the rider has navigation they trust on
 * the same phone with their saved places and their traffic in it.
 *
 * The `https` form rather than `geo:`, because it resolves on both platforms
 * and in a desktop browser — and Android hands it straight to Maps anyway.
 */
export function directionsUrl(input: {
  latitude?: string | null;
  longitude?: string | null;
  query?: string | null;
}): string {
  const base = 'https://www.google.com/maps/dir/?api=1&destination=';

  /*
   * Coordinates beat the address text whenever there are any. A geocoder
   * searching "Flat 3B, Behala" is how a delivery reaches the right street and
   * the wrong building, and the pin is what the customer actually dropped.
   */
  if (input.latitude && input.longitude) {
    return `${base}${encodeURIComponent(`${input.latitude},${input.longitude}`)}`;
  }
  return `${base}${encodeURIComponent(input.query ?? '')}`;
}

type Place = { latitude?: string | null; longitude?: string | null; query?: string | null };

function placeParam(place: Place): string {
  return place.latitude && place.longitude
    ? `${place.latitude},${place.longitude}`
    : (place.query ?? '');
}

/**
 * The whole leg, shop to door, opened in the rider's maps app.
 *
 * Unlike `directionsUrl` this pins the ORIGIN to the pharmacy rather than to
 * wherever the phone is, so it draws the trip the rider was quoted — the same
 * two points the server measured. On a phone the https form hands straight
 * to the Maps app; `two-wheeler` is the mode a rider is actually on, and Maps
 * falls back to driving wherever it does not offer it.
 */
export function routeUrl(origin: Place, destination: Place): string {
  const params = new URLSearchParams({
    api: '1',
    origin: placeParam(origin),
    destination: placeParam(destination),
    travelmode: 'two-wheeler',
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * The dialler, not an in-app call.
 *
 * The rider's own dialler has the call log, the speakerphone and the redial,
 * and none of that is worth rebuilding. What matters is that the number was
 * WITHHELD until pickup — the button simply does not exist before then.
 */
export function telUrl(phone: string): string {
  // Spaces and dashes are fine in a tel: URI but confuse some Android
  // diallers; everything but digits and a leading plus is dropped.
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

/** The address on one line, for a maps query and for the screen. */
export function addressOneLine(address: DeliveryAddress | null | undefined): string {
  if (!address) return '';
  return [address.line1, address.line2, address.landmark, address.city, address.pincode]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part && part.length > 0))
    .join(', ');
}

/** Whether the customer dropped a pin, as opposed to only typing an address. */
export function isPinned(address: DeliveryAddress | null | undefined): boolean {
  return Boolean(address?.latitude && address?.longitude);
}
